import { IdentityRequestType } from '@/lib/apollo/mutation/createIdentityDataRequest/mutation'
import { SectionName, DocumentType } from '@/lib/apollo/query/get-document-requests'

import type { DocumentRequestConfig, RefereeDetails } from './types'

export const buildPromptRefereeMessage = (refereeDetails?: RefereeDetails): string => {
  const refereeContactSentence = refereeDetails
    ? ` We tried to reach ${refereeDetails.name} using the contact details we have (${
        refereeDetails.email ? `${refereeDetails.email}, ` : ''
      }${refereeDetails.mobile}), but haven't heard back.`
    : ''

  return `We've reviewed the documents you've provided, thank you.

We're now waiting on a response from your landlord/letting agent to complete your reference.${refereeContactSentence}

Please could you follow up with them and ask them to complete the reference request as soon as possible?

Once we hear back from them, we'll be able to progress with your referencing.`
}

const organisationDocumentRequestConfigs: DocumentRequestConfig[] = [
  {
    documentType: DocumentType.freeText,
    emailSubject: '',
    defaultRequestMessage: '',
    defaultInternalComment: '',
    corporateLetTenant: true,
    sectionName: SectionName.organisation,
  },
]

const financeDocumentRequestConfigs: DocumentRequestConfig[] = [
  {
    documentType: DocumentType.freeText,
    emailSubject: '',
    defaultRequestMessage: '',
    defaultInternalComment: '',
    corporateLetTenant: true,
    sectionName: SectionName.financial,
  },
]

const corporateLetTenantIdentityDocumentRequestConfigs: DocumentRequestConfig[] = [
  {
    documentType: DocumentType.letterOfAuthority,
    emailSubject: 'Letter of Authority required',
    defaultRequestMessage: `We are processing the tenancy application for xxx and require some more information to be able to proceed with the referencing process:

The named signee, is not an active Officer listed on the relevant Public Register i.e. Companies House. Please, provide a letter of delegated authority, stating the signee's full name and position, and confirming they are authorised to sign tenancy agreements on the Organisation's behalf. The document should be a headed pdf, dated within the last 12 months and signed by an active Officer listed on the Public Register.`,
    defaultInternalComment:
      'A Letter of authority has been requested and the applicant has received an email update detailing this.',
    corporateLetTenant: true,
    sectionName: SectionName.identity,
  },
  {
    documentType: DocumentType.appropriateId,
    emailSubject: 'Appropriate ID required',
    defaultRequestMessage: `We are processing the tenancy application for xxx and require some more information to be able to proceed with the referencing process:

The ID image provided isn't clear enough to pass our authentication service. Please provide a new version and ensure that the whole document is visible without any obstructions or glare.`,
    defaultInternalComment:
      'An Appropriate ID has been requested and the applicant has received an email update detailing this.',
    corporateLetTenant: true,
    sectionName: SectionName.identity,
  },
  {
    documentType: DocumentType.freeText,
    emailSubject: '',
    defaultRequestMessage: '',
    defaultInternalComment: '',
    corporateLetTenant: true,
    sectionName: SectionName.identity,
  },
]

const identityDocumentRequestConfigs: DocumentRequestConfig[] = [
  {
    documentType: DocumentType.mrzMissing,
    emailSubject: 'Clearer passport image required',
    defaultRequestMessage:
      'The passport document you have provided is not sufficient as the Machine Readable Zone (MRZ) is not fully visible. The MRZ is the two lines of letters, numbers and symbols at the bottom of the photo page of your passport,\n\nPlease upload a clear, full image of the passport photo page with all four corners visible, ensuring the entire MRZ is visible and not cut off, covered, blurred or obscured.',
    defaultInternalComment:
      'Clearer passport image has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.shareCode,
    emailSubject: 'Right to Rent Information Required',
    defaultRequestMessage: `As you are a non-UK citizen wishing to rent in England, we require a valid document to enable the letting agency to complete the Right to Rent checks.

Please provide one of the following:

- 9-digit share code: This can be obtained from [gov.uk/prove-right-to-rent](https://www.gov.uk/prove-right-to-rent). Please share the 9-digit code itself. A screenshot or photo of your eVisa page can't be used.

- A valid, in-date vignette VISA. This is the entry clearance visa sticker found inside your passport.

- Proof of an exception. If you're exempt from providing the above (for example, international military personnel), see our [guide to providing evidence](https://tenanthelp.goodlord.co/s/article/ID-Providing-evidence-of-Right-to-Right-for-International-Military-personnel).`,
    defaultInternalComment:
      'Share code has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.imageQuality,
    emailSubject: 'Clearer ID document required',
    defaultRequestMessage:
      'The ID document you have provided is not sufficient due to the image quality. \n\nPlease upload a clear copy of your ID with all four corners visible and free from glare or shadows.',
    defaultInternalComment:
      'Clearer ID document has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.proofOfNameChange,
    emailSubject: 'Proof of name change required',
    defaultRequestMessage:
      'The name on the tenancy does not match all or one of the ID documents provided. Please provide proof of name change, such as a marriage or deed-poll certificate.',
    defaultInternalComment:
      'Proof of name change has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.inDateDrivingLicence,
    emailSubject: 'In-date UK driving license required',
    defaultRequestMessage:
      "Unfortunately, we're unable to accept your driver's licence because it's expired.\nWe can only use an in-date UK driving licence for your identity check.\nPlease upload a photo of your current, valid driving licence.\n\nIf you don't have one, the following are also accepted:\n\n- Passport\n\n- UK Birth Certificate\n\n- Letter of Attestation\n\nhttps://tenanthelp.goodlord.co/s/article/Documents-to-submit-to-pass-your-ID-check",
    defaultInternalComment:
      'In-date UK driving license has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.supplimentaryId,
    emailSubject: 'Additional ID documents required',
    defaultRequestMessage: `In line with government guidelines we require a second ID document to support the one you have already provided.

A full list can be obtained via this link: https://www.gov.uk/prove-right-to-rent/using-other-documents

However, the most common acceptable documents are:

- Passport

- In-date UK driving licence

- UK Birth Certificate

- Letter of Attestation

- Benefits paperwork dated within the last 3 months`,
    defaultInternalComment:
      'Additional ID documents have been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.selfie,
    emailSubject: 'Selfie image required',
    defaultRequestMessage:
      'To complete your identity verification, please upload a selfie.\n\nYour selfie needs to:\n\n- Clearly show your face, matching the photo on your ID document.\n\n- Be taken live, not a photo of a photo, or an image from your camera roll.',
    defaultInternalComment: 'Selfie has been requested for identity verification.',
  },
  {
    documentType: DocumentType.guarantorPaperIdDocumentsProvided,
    emailSubject: 'Photo ID required',
    defaultRequestMessage:
      "As a guarantor, we're unable to accept paper forms of ID.\nPlease upload a photo ID document instead.\n\nThis can be one of the following:\n\n- Passport\n\n- In-date UK driving licence\n\nYou can find how to complete the Identity section as a guarantor here: https://guarantorhelp.goodlord.co/s/article/GG-ID-guide",
    defaultInternalComment:
      'Photo ID has been requested to verify the identity of the applicant.',
  },
  {
    documentType: DocumentType.bothSidesOfPhotoId,
    emailSubject: 'Full ID document required',
    defaultRequestMessage:
      "Thanks for uploading your photo ID. Since this document contains information on both sides, we need to see both sides to complete your identity check.\n\nPlease upload a clear photo of the back/front of your ID as well with all four corners visible and free from glare or shadows.\n\nOnce we've received it, we'll continue processing your reference.",
    defaultInternalComment: 'Front & back of the ID document requested.',
  },
  {
    documentType: DocumentType.idDoc,
    emailSubject: 'ID document required',
    defaultRequestMessage: `We have not yet received an acceptable proof of identity document. Please upload an identity document as outlined in our help centre link below.

This can be, for example:

- Passport

- In-date UK driving licence

- UK Birth Certificate

https://tenanthelp.goodlord.co/s/article/Documents-to-submit-to-pass-your-ID-check`,
    defaultInternalComment:
      'No ID document has been provided and applicant has been sent an email requesting the same.',
  },
  {
    documentType: DocumentType.other,
    emailSubject: 'Additional information required',
    defaultRequestMessage: '',
    defaultInternalComment: '',
  },
].map(config => ({
  ...config,
  sectionName: SectionName.identity,
  corporateLetTenant: false,
}))

export const UNSUPPORTED_DOCUMENT_HELP_CENTRE_LINK =
  'https://tenanthelp.goodlord.co/article?urlName=Documents-to-submit-to-pass-your-ID-check'

const ID_VERIFICATION_EMAIL_SUBJECT = 'Action Required: Complete your ID Verification'

const toIdVerificationRetryMessage = (reason: string): string =>
  `We need you to complete your identity check again because we weren’t able to successfully verify your identity using your previous submission.


Your previous submission couldn’t be accepted for the following reason:

${reason}

What you need to do:

Please complete the new identity check request you have received and follow the on-screen instructions. You’ll need to complete the full identity check again, even if you’ve already submitted your document previously.

To give your check the best chance of completing successfully:

- Use your original, valid identity document.
- Make sure all document details are clearly visible and all four corners of the document are in the image.
- Avoid blur, glare, shadows or covering any part of the document.
- Complete the check somewhere well lit.
- Make sure your identity document is supported, using our Help Centre guide if needed.
- Follow the on-screen instructions carefully when capturing your document and completing the face check.

You don’t need to send us a separate photo, scan or selfie. Please complete the full identity check through the new request instead.

Once completed, we’ll receive the result automatically and your referencing can continue.

If you experience any technical difficulties completing the check, please contact us for support.`

const identityVerificationRequestConfigs: DocumentRequestConfig[] = [
  {
    documentType: DocumentType.completeIdVerification,
    identityRequestType: IdentityRequestType.CompleteIdVerification,
    defaultRequestMessage: `We need you to complete an identity check so we can verify your identity as part of your referencing application.

What you need to do:

Please complete the identity check request you have received and follow the on-screen instructions.

To give your check the best chance of completing successfully:

- Use your original, valid identity document.
- Make sure all document details are clearly visible.
- Avoid blur, glare, shadows or covering any part of the document.
- Complete the check somewhere well lit.
- Follow the on-screen instructions carefully when capturing your document and completing the face check.

You don’t need to send us a separate photo, scan or selfie. Please complete the full identity check through the request provided.

Once completed, we’ll receive the result automatically and your referencing can continue.

If you experience any technical difficulties completing the check, please contact us for support.`,
  },
  {
    documentType: DocumentType.completeIdVerificationImageQuality,
    identityRequestType: IdentityRequestType.CompleteIdVerificationImageQuality,
    defaultRequestMessage: toIdVerificationRetryMessage(
      'Image quality: The image was blurry or had glare, which meant we couldn’t clearly verify your identity document.',
    ),
  },
  {
    documentType: DocumentType.completeIdVerificationPartialImage,
    identityRequestType: IdentityRequestType.CompleteIdVerificationPartialImage,
    defaultRequestMessage: toIdVerificationRetryMessage(
      'Image cut off: Part of your identity document was cut off in the image. We need to see all four corners of the document to complete the check.',
    ),
  },
  {
    documentType: DocumentType.completeIdVerificationUnsupportedDocument,
    identityRequestType: IdentityRequestType.CompleteIdVerificationUnsupportedDocument,
    defaultRequestMessage: toIdVerificationRetryMessage(
      `Unsupported document type: The identity document you submitted isn’t supported for this check. Please see our [Help Centre guide](${UNSUPPORTED_DOCUMENT_HELP_CENTRE_LINK}) for a list of accepted documents.`,
    ),
  },
  {
    documentType: DocumentType.completeIdVerificationFaceCheckRetry,
    identityRequestType: IdentityRequestType.CompleteIdVerificationFaceCheckRetry,
    defaultRequestMessage: `We need you to complete your identity check again because we weren’t able to successfully verify your identity using your previous submission.

Your previous submission couldn’t be accepted for the following reason:

Face check could not be confirmed: We weren’t able to successfully complete the face verification from your previous check. This can sometimes happen if the image quality, lighting, device used or another person being visible affects the check.

What you need to do:

Please complete the new identity check request you have received and follow the on-screen instructions. You’ll need to complete the full identity check again, even if you’ve already submitted your document previously.

To give your check the best chance of completing successfully:
- Where possible, complete the check using a mobile phone rather than a computer.
- Make sure you are in a well-lit area, avoiding glare, shadows or strong light behind you.
- Make sure only you are visible when completing the face check.
- Make sure the person completing the face check is the same person whose identity document is being submitted.
- Keep your face clearly visible and follow the on-screen instructions carefully.
- Use your original, valid identity document.
- Make sure your document details are clearly visible and all four corners of the document are in the image.
You don’t need to send a separate photo, scan or selfie. Please complete the full identity check through the new request instead.

Once completed, we’ll receive the result automatically and your referencing can continue.

If you experience any technical difficulties completing the check, please contact us for support.`,
  },
].map(config => ({
  ...config,
  emailSubject: ID_VERIFICATION_EMAIL_SUBJECT,
  defaultInternalComment:
    'ID verification has been requested and the applicant has received an email update detailing this.',
  sectionName: SectionName.identity,
  corporateLetTenant: false,
}))

const creditDocumentRequestConfigs: DocumentRequestConfig[] = [
  {
    documentType: DocumentType.proofOfSatisfiedAdverseCredit,
    emailSubject: 'Proof of satisfied adverse credit required',
    defaultRequestMessage:
      "We have been requested by the agent to request / obtain proof that your adverse credit has been satisfied. Please upload a letter of satisfaction directly from the court, or from a solicitor that states your name, amount of CCJ as well as it being satisfied, as well as the court reference number. Ideally, please upload the satisfaction document that's provided by the court directly as it will show everything we need, but a headed solicitors letter can be acceptable as well if all of the above details are included. We can also accept an email from the solicitors if the requested details are visible but we will need you to upload a PDF copy of the email directly to us (a screenshot is not sufficient). This should include the entire email thread.",
    sectionName: SectionName.credit,
    defaultInternalComment:
      'Proof of satisfied adverse credit has been requested and the applicant has received an email update detailing this.',
    corporateLetTenant: false,
  },
  {
    documentType: DocumentType.outstandingCreditDocument,
    emailSubject: 'Additional information required',
    defaultRequestMessage: '',
    sectionName: SectionName.credit,
    defaultInternalComment: '',
    corporateLetTenant: false,
  },
]

const residentialDocumentRequestConfigs: DocumentRequestConfig[] = [
  {
    documentType: DocumentType.promptReferee,
    emailSubject: 'Reminder: Prompt your referee for a reference',
    defaultRequestMessage: buildPromptRefereeMessage(),
    defaultInternalComment:
      'Reminder sent to applicant to prompt their referee for a reponse.',
  },
  {
    documentType: DocumentType.unacceptableReferee,
    emailSubject: 'Tenancy agreement & three bank statements required',
    defaultRequestMessage: `We have received a reference however it's not acceptable because:

- The referee is a family member

- The referee is an applicant/guarantor on the same tenancy

Please upload a copy of your fully signed tenancy agreement and the last 3 months of bank statements showing your name and the rental payments.`,
    defaultInternalComment:
      "Referee doesn't fulfill our criteria. Requested tenancy agreements and bank statements",
  },
  {
    documentType: DocumentType.tenancyAgreement,
    emailSubject: 'Tenancy agreement required',
    defaultRequestMessage: `Thank you for uploading your bank statements.

Please upload a full copy of your signed tenancy agreement for your current address. Please note, this must include your name, tenancy address, rental amount and the payment date.`,
    defaultInternalComment: 'Requested tenancy agreement.',
  },
  {
    documentType: DocumentType.tenancyAgreementAndBankStatements,
    emailSubject: 'Tenancy agreement & bank statements (rental payments) required',
    defaultRequestMessage: `Please provide a copy of your fully signed PDF tenancy agreement alongside the last 3 months of bank statements in PDF showing your most recent 3 months of rental payments being made.

Please note, on all occasions we need to see the most recent rental payment being made.

Once we have these details, we'll be able to progress your reference.`,
    defaultInternalComment:
      'Tenancy agreement and bank statements have been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.bankStatements,
    emailSubject: 'Bank statements required',
    defaultRequestMessage: `Thank you for providing your tenancy agreement.

We also require your last 3 months of bank statements, showing your name and your rental payments clearly.

These need to be full statements and a screenshot from your mobile banking app would not be sufficient, as it doesn't show enough detail to verify.

Once we have these, we'll be able to progress your referencing.

Please note: we can only accept the documents in PDF format.`,
    defaultInternalComment: 'Requested bank statements.',
  },
  {
    documentType: DocumentType.partialBankStatement,
    emailSubject: 'Additional bank statement required',
    defaultRequestMessage: `Thank you for providing your [INSERT MONTHS RECEIVED] bank statements showing your [INSERT RENTAL PAYMENTS WE CAN SEE].

As we require the most recent 3 months proof of rental payments being made shown in your bank statements, we still require [X BANK STATEMENT SHOWING X MONTH'S RENTAL PAYMENT]. Please can you provide this as soon as possible.

Please note that this must be a PDF bank statement, we are unable to use mobile screenshots. If you have not received your bank statement for [MOST RECENT MONTH], please provide your downloaded transaction statement.`,
    defaultInternalComment:
      'Additional bank statement has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.residentialRefereeContactDetails,
    emailSubject: 'Referee contact details required',
    defaultRequestMessage: `We need contact details for a residential referee to confirm your previous tenancy.

This can be either your landlord or the letting agent who managed the property.

Please provide:

- Referee name

- Company name (if a letting agent)

- Contact email address

- Contact phone number

Once we have these details, we'll be able to reach out and progress your reference.`,
    defaultInternalComment:
      'Referee contact details have been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.docsRequestAwaitingLandlordRef,
    emailSubject: 'Tenancy agreement & bank statements required',
    defaultRequestMessage: `Thank you for the details you've provided so far.

While we wait to hear back from your landlord, please could you also upload:

- A copy of your fully signed tenancy agreement for your current address

- Your last 3 months of bank statements, clearly showing your name and rental payments

Please note: we're unable to accept mobile banking screenshots, and it would help speed things up if you could highlight the rental payments on the statements.

If possible, it would also help to prompt your landlord or agent to respond to our reference request, as this can help speed up your application.

Once we have everything, we'll be able to progress with your referencing.`,
    defaultInternalComment:
      'Tenancy agreement and bank statements have been requested while awaiting a response from the landlord/letting agent.',
  },
  {
    documentType: DocumentType.outstandingResidentialDocument,
    emailSubject: 'Additional information required',
    defaultRequestMessage: '',
    defaultInternalComment: '',
  },
].map(config => ({
  ...config,
  sectionName: SectionName.residential,
  corporateLetTenant: false,
}))

const incomePayeDocumentRequestConfigs: DocumentRequestConfig[] = [
  {
    documentType: DocumentType.incompletePayslips,
    emailSubject: 'Payslips required',
    defaultRequestMessage: `We need to see your recent payslips to verify your income.

- If you're paid monthly, please upload your last 3 payslips.
- If you're paid weekly, please upload your last 6 payslips (covering the last 6 weeks).
- If you're paid fortnightly, please upload your last 6 payslips (covering the last 3 months).

Please note: we can only accept these documents in PDF format.`,
    defaultInternalComment: 'Payslips are incomplete. Further payslips requested.',
  },
  {
    documentType: DocumentType.partialPayslips,
    emailSubject: 'Additional Payslips Required',
    defaultRequestMessage: `Thank you for the payslips you've sent so far.

We've received your payslips for:
[Month/Year]
[Month/Year]

As we need your last 3 months to assess your income, please upload your payslip for:
[Month/Year]

Once we have this, we'll be able to progress with your referencing.`,
    defaultInternalComment:
      'Partial payslips received. Further payslips requested to complete the last 3 months.',
  },
  {
    documentType: DocumentType.ftcInformation,
    emailSubject: 'Signed employment contract required',
    defaultRequestMessage: `As you / your employer have declared you are on a fixed term contract, we need to see your fully signed employment contract confirming both your start date and end date.

Please upload a copy that clearly shows these details, along with your name, contract type, and salary or wage.

Once we have this, we'll be able to progress your referencing.`,
    defaultInternalComment:
      'Signed employment contract has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.incompleteContract,
    emailSubject: 'Signed employment contract required',
    defaultRequestMessage: `We're unable to verify your income using the contract you've provided because:

- It hasn't been signed, and/or

- It's missing some required information ([specify: e.g. salary, start date])

- It does not confirm your fixed term employment end date (if applicable)

Please upload a fully signed contract that clearly shows:

- Your name

- Your employment start date

- Contract type (e.g. permanent, fixed-term)

- Your employment end date (if applicable)

- Your salary or wage

Please note: we can only accept these documents in PDF format.`,
    defaultInternalComment:
      'Signed employment contract has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.employmentContractLessThan3Months,
    emailSubject: 'Signed employment contract required',
    defaultRequestMessage: `We are unable to complete with payslips as you are unable to provide the required 3 months.

We require you to upload your fully signed contract, including:

- Your name

- Your employment start date

- Contract type

- Salary or wage`,
    defaultInternalComment:
      'Signed employment contract has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.internationalEmployment,
    emailSubject: 'Confirmation of continued employment in the UK required',
    defaultRequestMessage: `We need confirmation from your employer that your international employment is continuing here in the UK.

Please upload one of the following:

- A signed and dated transfer letter

- An updated employment contract (or your original contract, if it already confirms remote working)

- A reference from your employer confirming your international employment is continuing (has to be signed by your employer)`,
    defaultInternalComment:
      'Confirmation of continued employment in the UK has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.noIncomeOnOpenBanking,
    emailSubject: 'Open Banking reconnection required',
    defaultRequestMessage: `We're unable to progress your referencing because the account you've connected (ending XXXX) doesn't show any income from [Employer/Income Source].

To continue, please reconnect open banking using the account where you actually receive your income from [Employer/Income Source].`,
    defaultInternalComment:
      'Open Banking reconnection has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.employmentContract,
    emailSubject: 'Employment contract required',
    defaultRequestMessage: `We need your fully signed employment contract/Offer letter to verify your income and start date.

Please upload a contract that clearly shows:

- Your name

- Your employment start date

- Contract type (e.g. permanent, fixed-term)

- Your salary or wage

- Signed by both you and your employer

Please note: we can only accept these documents in PDF format.`,
    defaultInternalComment: 'Employment contract/ offer letter requested.',
  },
  {
    documentType: DocumentType.payePromptReferee,
    emailSubject: 'Reminder: Prompt your referee for a reference',
    defaultRequestMessage: `We're still waiting to hear back from your employer or income referee, who we contacted to confirm your income details.

Please let them know this has been sent to the email address provided: [EMAIL HERE].

If this doesn't look right, please let us know so we can correct it and resend the request.

Please could you follow up with them directly and ask them to respond as soon as possible?

Once we hear back, we'll be able to continue with your referencing.`,
    defaultInternalComment:
      'Reminder sent to applicant to prompt their employer/income referee for a response.',
  },
  {
    documentType: DocumentType.payeRefereeContactDetails,
    emailSubject: 'Referee contact details required',
    defaultRequestMessage: `Before we can proceed with your reference, we kindly require your employment referee's contact details. Please provide:

- Name of your referee

- Position: this needs to be someone in HR, payroll, or your line manager

- Contact phone number

- Contact email address: this must be a work email linked to your employer's domain (e.g. name/department@employer.com), rather than a personal email address

Once we have these details, we'll be able to reach out and progress your reference.`,
    defaultInternalComment:
      'Referee contact details have been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.payeSickPay,
    emailSubject: 'Return to work date required',
    defaultRequestMessage: `We noticed that your recent payslips show you've received sick pay.

To progress your income verification, please provide a return to work date.

This can be confirmed in one of the following ways:

- An official letter or email from your employer confirming the date, or

- Your employer contacting us directly to confirm this on your behalf.`,
    defaultInternalComment:
      'Return to work date has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.outstandingIncomeDocument,
    emailSubject: 'Additional information required',
    defaultRequestMessage: '',
    defaultInternalComment: '',
  },
].map(config => ({
  ...config,
  sectionName: SectionName.income,
  corporateLetTenant: false,
}))

const incomeOtherDocumentRequestConfigs: DocumentRequestConfig[] = [
  {
    documentType: DocumentType.insufficientBankStatement,
    emailSubject: 'Additional bank statement required',
    defaultRequestMessage: `We have been unable to confirm your income verification with your bank statement because:

- It is not dated in the last 30 days

- We do not accept mobile banking screenshots (we can only accept bank statements in PDF format)

- It does not contain your name

- It does not contain the closing balance

We require you to upload a PDF Bank Statement ensuring that it meets the below criteria:

- Dated in the last 30 days

- Contains your name

- Clearly shows the closing balance`,
    defaultInternalComment:
      'Additional bank statement has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.studentLoan,
    emailSubject: 'Bursary/Scholarship/Stipend award letter required',
    defaultRequestMessage: `We have been unable to verify your student income with the document provided because:

- Your Bursary/Scholarship/Stipend has ended

- We cannot accept screenshots of email chains

- Maintenance loan funding from Student Finance in the UK or any international loan organisation

- Tuition fee funding from Student Finance in the UK

If you are in receipt of a Bursary/Scholarship/Stipend for your studies please provide your award letter. Please ensure the document shows the following:

- A headed and signed document from a legitimate funding source

- Start date of funding

- End date of funding

- Value of the funding award`,
    defaultInternalComment:
      'Bursary/Scholarship/Stipend award letter has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.benefits,
    emailSubject: 'Benefits documents required',
    defaultRequestMessage: `We're unable to complete verification of your benefits because:

- We've only received [XXXX], but we also need [XXXX]

- The documents provided don't clearly show your full name and the benefit payment amounts

To complete this, please provide one of the following:

- Your bank statements for [XXXX], clearly showing your name and the benefit payments.

- A copy of your benefits award letter (for example, your PIP award letter, if applicable).

- Your last 3 months of Universal Credit statements, clearly showing your name and the benefit payments.

In all cases, we need to be able to see the frequency and amount of your payments.

Once we have this, we'll be able to progress your referencing.`,
    defaultInternalComment:
      'Bank statements have been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.incompletePensionDoc,
    emailSubject: 'Additional pension docs required',
    defaultRequestMessage: `We have been unable to complete your income verification for your pension because:

- The documents provided do not [INSERT REASON]

Please provide us with one of the following:

- Last 3 months bank statements showing your name and pension payments

- Last 3 months worth of your most recent pension payslips (List months)

- Most recent annual pension letter/statement

- Pension P60

- Self Assessment Tax Return along with proof of submission

You can find more information on the documents we require to verify your pension via this link:
https://tenanthelp.goodlord.co/s/article/Income-Verifying-your-income-with-pension-statements`,
    defaultInternalComment:
      'Bank statements or payslips have been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.jointAccountPension,
    emailSubject: 'Additional pension documents required',
    defaultRequestMessage: `We have been unable to complete your income verification for your pension because the documents provided are in joint names. Please upload your pension P60, pension payslips or a letter from the pension provider.`,
    defaultInternalComment:
      'Additional pension documents have been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.proofOfSubmission,
    emailSubject: 'Proof of tax submission required',
    defaultRequestMessage: `Thank you for providing your tax return. We now need proof that it's been submitted to HMRC.

Please provide one of the following:

- HMRC Tax Year Overview

- A 100% complete HMRC Tax Calculation

- Submission receipt from HMRC (For a submission receipt we accept a HMRC tax year overview, 100% complete HMRC tax calculation, tax summary or your submission receipt from HMRC.)

To get your Tax Year Overview: log in to your HMRC online account → Self Assessment → More details about your returns and payments → View previously filed returns → select the most recent year → View Tax Year Overview → Print Overview → Save as PDF.

Need more help? See our guides on self-employment verification: https://tenanthelp.goodlord.co/s/article/Income-How-we-verify-your-self-employment and obtaining documents from HMRC: https://contact.goodlord.co/tag/s/article/Obtaining-documents-from-HMRC

Once we have this, we'll be able to progress your referencing.`,
    defaultInternalComment:
      'Proof of tax return submission has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.maternityLeave,
    emailSubject: 'Confirmation of parental leave details required',
    defaultRequestMessage: `We can see from the documents provided that you're currently receiving parental leave payments.

To progress your income verification, please upload a letter from your employer confirming:

- Your leave start date

- Your confirmed return to work date, if known

- Your pay and pay schedule during leave

- Your pay once you return to work, if known`,
    defaultInternalComment:
      'Confirmation of parental leave details has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.dividendsConfirmation,
    emailSubject: 'Confirmation of dividends required',
    defaultRequestMessage: `From the documents provided we have been able to verify your PAYE income. If you would like to include any dividends payment we require either your SATR and proof of submission or a reference from the an HR or Payroll adviser or your accountant.`,
    defaultInternalComment:
      'Confirmation of dividends has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.thirdPartyConfirmation,
    emailSubject: 'Third party confirmation required',
    defaultRequestMessage: `We can accept a third party such as a company paying rent on your behalf.

To confirm this, please provide a signed letter on company-headed paper, stating:

- The amount the company will be paying

- Who the payment is for

- The dates the payments will start from

- How long the payment will continue

Once we have this, we'll be able to complete your verification.`,
    defaultInternalComment:
      'Third party confirmation has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.nonUKSelfEmployment,
    emailSubject: 'Evidence of savings or UK employment/self employment required',
    defaultRequestMessage: `We have been unable to accept international income for self employment and would require you to provide us with savings or evidence of UK employment/ self employment.`,
    defaultInternalComment:
      'Evidence of savings or UK employment/ self employment has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.incompleteOldTaxReturn,
    emailSubject: 'HMRC self-assessment tax return required',
    defaultRequestMessage: `Thank you for submitting your tax return. Unfortunately, we're unable to verify your income because:

- [We've only received a partial tax return]

- [We've received an older tax return than we can accept]

Please provide both of the following:

- Your most recently submitted HMRC self-assessment tax return

- A submission receipt confirming it was filed with HMRC (For a submission receipt we accept a HMRC tax year overview, 100% complete HMRC tax calculation, tax summary or your submission receipt from HMRC.)

To get your Tax Year Overview: log in to your HMRC online account → Self Assessment → More details about your returns and payments → View previously filed returns → select the most recent year → View Tax Year Overview → Print Overview → Save as PDF.

Please note: we can only accept tax returns for the 20XX – 20XX or 20XX – 20XX tax years.

For how to obtain your documents please follow this link: https://tenanthelp.goodlord.co/s/article/Obtaining-documents-from-HMRC

Alternatively, we can accept a reference from your professional accountant instead. If you'd prefer this route, please provide:

- Their full name and accountancy firm

- A landline or business contact number (not a mobile number)

- A professional email address: We're unable to accept personal domains such as Gmail, Yahoo, Hotmail, or iCloud

Once we have everything, we'll be able to progress your referencing.`,
    defaultInternalComment:
      'HMRC self-assessment tax return has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.subcontractorDocuments,
    emailSubject: 'Employment contract required',
    defaultRequestMessage: `As you are a subcontractor/contractor we require the following to verify your income:

- Signed copy of your employment contract

-  Your last 3 months of payslips/invoices`,
    defaultInternalComment:
      'Employment contract/ payslips/ invoices have been requested.',
  },
  {
    documentType: DocumentType.taxReturnWithProofOfSubmission,
    emailSubject: 'Submission receipt required',
    defaultRequestMessage: `Please provide your most recently submitted HMRC self-assessment tax return with a copy of the submission receipt. Please note we only accept the XXX and XXX tax returns.

For a submission receipt we accept a HMRC tax year overview, 100% complete HMRC tax calculation, tax summary or your submission receipt from HMRC.

For how to obtain your documents please follow this link: https://tenanthelp.goodlord.co/s/article/Obtaining-documents-from-HMRC`,
    defaultInternalComment: 'Tax return with proof of submission requested.',
  },
  {
    documentType: DocumentType.taxReturnOnly,
    emailSubject: 'HMRC self-assessment tax return required',
    defaultRequestMessage: `We have received your XXX (proof of submission) however we are still outstanding your Self Assessment Tax Return. Please provide your most recently submitted HMRC self-assessment tax return (SA100). Please note we only accept the XXX and XXX tax returns. You can refer to our Help Centre as a guide to the documents we accept: https://tenanthelp.goodlord.co/s/article/Obtaining-documents-from-HMRC`,
    defaultInternalComment:
      'Self-assessment tax return has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.accountantPrompt,
    emailSubject: 'Reminder: Prompt your accountant for a reference',
    defaultRequestMessage: `We're still waiting to hear back from your Accountant or referee about the reference request we emailed them.

Please could you follow up with them directly and ask them to complete it as soon as possible?

This may help to speed up your application.`,
    defaultInternalComment:
      'Reminder sent to applicant to prompt their accountant/referee for a response.',
  },
  {
    documentType: DocumentType.accountantsDetails,
    emailSubject: 'Accountant details required',
    defaultRequestMessage: `Please can you provide details of your accountant who can confirm your earnings, we require:

- Company Name
- Company Address
- Referee Name
- Professional email address (we cannot accept unprofessional domains like Gmail or Yahoo)
- Contact Phone number`,
    defaultInternalComment:
      'Accountant details have been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.investments,
    emailSubject: 'Investment portfolio document required',
    defaultRequestMessage: `We need a document confirming your investment balance to verify your income.

Please upload a statement that clearly shows:

- Your name
- Your current balance
- A date within the last 30 days

Once we have this, we'll be able to progress with your referencing.`,
    defaultInternalComment:
      'Applicant has been requested to provide documents showing their investments (cash element).',
  },
  {
    documentType: DocumentType.universalCredit,
    emailSubject: 'Universal Credit statements required',
    defaultRequestMessage: `We need your last 3 months of Universal Credit statements to verify your income.

Please note: we need each individual monthly statement for the last 3 months, not a screenshot of your overview page showing your payment history.

You can get these from your Universal Credit online account:

- Log in to your Universal Credit portal.
- Select "Print Statement."
- Choose "Save as PDF" and upload the file to us.

Once we have these, we'll be able to progress with your referencing.`,
    defaultInternalComment:
      'Universal Credit statements have been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.obTsrSecondChance,
    emailSubject: 'Open Banking connection required',
    defaultRequestMessage: `We still need you to connect your bank account via Open Banking to verify your [Salary/income/pension/benefits payments].

We've sent a separate email with a secure link to connect the account where you receive this income. Please use that link to connect as soon as possible.

Once connected, we'll be able to progress with your referencing.`,
    defaultInternalComment:
      'Open Banking reconnection has been requested and the applicant has received an email update detailing this.',
  },
  {
    documentType: DocumentType.konfirTsrSecondChance,
    emailSubject: 'Payroll/HMRC connection required',
    defaultRequestMessage: `We still need you to connect to Payroll/HMRC to verify your employment income.

We've sent a separate email with a secure link to complete this connection.

Please use that link to connect your HMRC/Payroll record as soon as possible.

Once connected, we'll be able to progress with your referencing.`,
    defaultInternalComment:
      'Payroll/HMRC reconnection has been requested and the applicant has received an email update detailing this.',
  },
].map(config => ({
  ...config,
  sectionName: SectionName.income,
  corporateLetTenant: false,
}))

const documentRequestConfigs: DocumentRequestConfig[] = [
  ...financeDocumentRequestConfigs,
  ...organisationDocumentRequestConfigs,
  ...corporateLetTenantIdentityDocumentRequestConfigs,
  ...identityDocumentRequestConfigs,
  ...identityVerificationRequestConfigs,
  ...creditDocumentRequestConfigs,
  ...residentialDocumentRequestConfigs,
  ...incomePayeDocumentRequestConfigs,
  ...incomeOtherDocumentRequestConfigs,
]

export default documentRequestConfigs
